import { useState, useEffect, useRef } from 'react';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { MapPin, Navigation, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import Loading from './Loading';

const libraries = ['places'];

// Debug: Log the API key (remove in production)
console.log('Google Maps API Key exists:', !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
console.log('API Key length:', import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.length);

export default function LocationSearch({ onLocationSelect, selectedLocation }) {
    const toast = useToast();
    const { isLoaded, loadError: scriptLoadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        libraries: libraries
    });

    const [autocomplete, setAutocomplete] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [loadError, setLoadError] = useState(null);
    const [fetchingLocation, setFetchingLocation] = useState(false);

    useEffect(() => {
        if (selectedLocation?.address) {
            setInputValue(selectedLocation.address);
        }
    }, [selectedLocation]);

    const onLoad = (autoC) => {
        console.log('Autocomplete loaded successfully');
        setAutocomplete(autoC);
    };

    const onError = (error) => {
        console.error('Google Maps load error:', error);
        setLoadError('Failed to load Google Maps. Please check API key.');
    };

    const onPlaceChanged = () => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            console.log('Place selected:', place);

            if (place.geometry && place.geometry.location) {
                const location = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                    address: place.formatted_address || place.name,
                    placeId: place.place_id
                };

                onLocationSelect(location);
            } else {
                toast.warning('Please select a valid location from the dropdown');
            }
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setFetchingLocation(true);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;

                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode(
                        { location: { lat: latitude, lng: longitude } },
                        (results, status) => {
                            setFetchingLocation(false);
                            if (status === 'OK' && results[0]) {
                                const location = {
                                    lat: latitude,
                                    lng: longitude,
                                    address: results[0].formatted_address,
                                    placeId: results[0].place_id
                                };
                                setInputValue(results[0].formatted_address);
                                onLocationSelect(location);
                            } else {
                                toast.error('Failed to resolve address. Please search manually.');
                            }
                        }
                    );
                },
                (error) => {
                    setFetchingLocation(false);
                    console.error('Error getting location:', error);
                    toast.error('Unable to get your location. Please search manually.');
                }
            );
        } else {
            toast.error('Geolocation is not supported by your browser');
        }
    };

    const clearLocation = () => {
        setInputValue('');
        onLocationSelect(null);
    };

    // Show error if API key is missing
    if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
        return (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                <p className="font-medium">⚠️ Google Maps API Key Missing</p>
                <p className="text-sm mt-1">Please check your .env file and restart the dev server.</p>
            </div>
        );
    }

    if (loadError || scriptLoadError) {
        return (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                <p className="font-medium">⚠️ {loadError || 'Failed to load Google Maps API'}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-sm underline"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!isLoaded) {
        return <Loading mode="inline" message="Initializing Maps..." />;
    }

    return (
        <div className="space-y-4">
            {/* Current Location Button */}
            <button
                type="button"
                onClick={getCurrentLocation}
                disabled={fetchingLocation}
                className="w-full flex items-center justify-center space-x-2 bg-blue-50 text-blue-700 py-3 px-4 rounded-xl border border-blue-100 hover:bg-blue-100 transition-all font-semibold disabled:opacity-50"
            >
                {fetchingLocation ? (
                    <Loading mode="button" message="Pinpointing your location..." />
                ) : (
                    <>
                        <Navigation className="w-4 h-4" />
                        <span>Use Current Location</span>
                    </>
                )}
            </button>

            <div className="relative">
                <label className="block text-sm font-medium mb-1">
                    Search Delivery Location *
                </label>
                <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Autocomplete
                        onLoad={onLoad}
                        onPlaceChanged={onPlaceChanged}
                        options={{
                            componentRestrictions: { country: 'in' },
                            types: ['geocode', 'establishment']
                        }}
                    >
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Search for area, street, landmark..."
                            className="w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                    </Autocomplete>
                    {inputValue && (
                        <button
                            type="button"
                            onClick={clearLocation}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    Start typing to see suggestions (e.g., "Anna Nagar", "T Nagar")
                </p>
            </div>

            {/* Selected Location Display */}
            {selectedLocation && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <div className="flex items-start space-x-2">
                        <MapPin className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium text-green-900">Delivery Location Set</p>
                            <p className="text-sm text-green-700 mt-1">{selectedLocation.address}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}